import type { AccessResponse } from '@1webit/cvs-api-scheme';
import type { queryOptions } from '@tanstack/react-query';

import { getApiV1AddressBookUserRights } from '@/query/address-book';
import { getApiV1BankUserRights } from '@/query/bank';
import { getApiV1BeachUserRights } from '@/query/beach';
import { getApiV1ClubUserRights } from '@/query/club';
import { getApiV1CompetitionUserRights } from '@/query/competition';
import { getApiV1ListUserRights } from '@/query/lists';
import { getApiV1RequestUserRights } from '@/query/office';
import { getApiV1RefereeUserRights } from '@/query/referee';
import { getApiV1RosterUserRights } from '@/query/roster';
import type { TPermissions } from '@/types';
import { ModuleTag } from '@/types';
import { checkParamsId } from '@/utils';

export enum RightOptions {
  ADDRESS_BOOK = 'DIRECTORY2',
  CLUB = 'CLUB',
  COMPETITION = 'COMPETITION',
  ROSTER = 'ROSTER',
  REFEREE = 'REFEREE',
  CHARTER = 'CHARTER',
  DELEGATES = 'DELEGATES',
  REGISTER = 'REGISTER',
  TRANSFER = 'TRANSFER',
  TRANSFERS = 'TRANSFERS',
  QUALIFICATION = 'QUALIFICATION',
  LICENSE = 'LICENSE',
  BEACH = 'BEACH',
  BEACH_FOLDERS = 'BEACH_FOLDERS',
  BEACH_DELEGATION = 'BEACH_DELEGATION',
  BEACH_REFEREE = 'BEACH_REFEREE',
  BEACH_STATISTIC = 'BEACH_STATISTIC',
  REGISTRY = 'REGISTRY',
  BANK = 'BANK',
  COMMUNICATION = 'COMM',
  SUPER_LINKS = 'SUPER_LINK',
  SUPER_ROSTER = 'SUPER_ROSTER',
  SURVEY = 'SURVEY',
  VOTING = 'VOTING',
  NSA = 'NSA',
  SPORTS_FIELD = 'SPORTSFIELD',
  MEMBERSHIP_PAYMENTS = 'CONTRIBUTION',
  PLAYERS = 'PLAYER',
  FAMOUS_PEOPLE = 'FAMOUS_PERSON',
}

export const moduleHeaders: Record<RightOptions, string> = {
  [RightOptions.ADDRESS_BOOK]: 'Přehled práv adresářů',
  [RightOptions.CLUB]: 'Přehled práv odílů',
  [RightOptions.COMPETITION]: 'Přehled práv soutěží',
  [RightOptions.ROSTER]: 'Přehled práv soupisek',
  [RightOptions.REFEREE]: 'Přehled práv rozhodčích',
  [RightOptions.CHARTER]: 'Přehled práv listin',
  [RightOptions.DELEGATES]: 'Přehled práv delegátů',
  [RightOptions.REGISTER]: 'Přehled práv registrací',
  [RightOptions.TRANSFER]: 'Přehled práv přestupů',
  [RightOptions.TRANSFERS]: 'Přehled práv transferů',
  [RightOptions.QUALIFICATION]: 'Přehled práv kvalifikací',
  [RightOptions.LICENSE]: 'Přehled práv licencí',
  [RightOptions.BEACH]: 'Přehled práv beache',
  [RightOptions.BEACH_FOLDERS]: 'Přehled práv beachových složek',
  [RightOptions.BEACH_DELEGATION]: 'Přehled práv beach delegací',
  [RightOptions.BEACH_REFEREE]: 'Přehled práv beach rozhodčích',
  [RightOptions.BEACH_STATISTIC]: 'Přehled práv beach statistik',
  [RightOptions.REGISTRY]: 'Přehled práv typu žádosti',
  [RightOptions.BANK]: 'Přehled práv banky',
  [RightOptions.COMMUNICATION]: 'Přehled práv komunikace',
  [RightOptions.SUPER_LINKS]: 'Přehled práv linků',
  [RightOptions.SUPER_ROSTER]: 'Přehled práv supersoupisky',
  [RightOptions.SURVEY]: 'Přehled práv ankety',
  [RightOptions.VOTING]: 'Přehled práv hlasování',
  [RightOptions.NSA]: 'Přehled práv NSA',
  [RightOptions.SPORTS_FIELD]: 'Přehled práv sportovišť',
  [RightOptions.MEMBERSHIP_PAYMENTS]: 'Přehled práv členských příspěvků',
  [RightOptions.PLAYERS]: 'Přehled práv hráčů',
  [RightOptions.FAMOUS_PEOPLE]: 'Přehled práv osobností',
};

export type RightModule = keyof TPermissions | 'player-transfer';

export const rightModuleTagOverrides: Partial<Record<RightModule, string>> = {
  [ModuleTag.USER]: 'registrationRights',
  [ModuleTag.TRANSFERS]: 'transfersRights',
  'player-transfer': 'transferRights',
};

export const getRightsViewTag = (moduleTag: RightModule): string => {
  const lowerFirst = moduleTag.charAt(0).toLowerCase() + moduleTag.slice(1);
  return rightModuleTagOverrides[moduleTag] ?? `${lowerFirst}Rights`;
};

export type ItemRightsQueryFn = (params: Record<string, string>) => ReturnType<typeof queryOptions<AccessResponse>>;

export const itemRightsQuery: Partial<Record<RightOptions, ItemRightsQueryFn>> = {
  [RightOptions.COMPETITION]: params => getApiV1CompetitionUserRights(checkParamsId(params.competitionId)),
  [RightOptions.CLUB]: params => getApiV1ClubUserRights(checkParamsId(params.clubId)),
  [RightOptions.BEACH_FOLDERS]: params => getApiV1BeachUserRights({ folderId: checkParamsId(params.folderId) }),
  [RightOptions.BEACH]: params => getApiV1BeachUserRights({ tournamentId: checkParamsId(params.tournamentId) }),
  [RightOptions.REFEREE]: params => getApiV1RefereeUserRights({ groupId: checkParamsId(params.groupId) }),
  [RightOptions.ROSTER]: params => getApiV1RosterUserRights(checkParamsId(params.rosterId), checkParamsId(params.competitionId).toString()),
  [RightOptions.ADDRESS_BOOK]: params => getApiV1AddressBookUserRights(checkParamsId(params.id)),
  [RightOptions.BANK]: params => getApiV1BankUserRights(checkParamsId(params.id)),
  [RightOptions.CHARTER]: params => getApiV1ListUserRights(checkParamsId(params.id)),
  [RightOptions.REGISTRY]: params => getApiV1RequestUserRights(checkParamsId(params.type)),
};
export const moduleTagToRightOptions: Partial<Record<RightModule, RightOptions>> = {
  [ModuleTag.COMPETITIONS]: RightOptions.COMPETITION,
  [ModuleTag.CLUBS]: RightOptions.CLUB,
  [ModuleTag.BEACH]: RightOptions.BEACH,
  [ModuleTag.REFEREE]: RightOptions.REFEREE,
  [ModuleTag.ROSTER]: RightOptions.ROSTER,
  [ModuleTag.DIRECTORY]: RightOptions.ADDRESS_BOOK,
  [ModuleTag.BANK]: RightOptions.BANK,
  [ModuleTag.LISTS]: RightOptions.CHARTER,
};
